<template>
  <section v-editable="blok" :class="getComponentClass(blok, animationClass)">
    <div class="container">
      <h3 v-if="blok.title" class="practices-list__title">{{ blok.title }}</h3>

      <p v-if="blok.description" class="practices-list__description">
        {{ blok.description }}
      </p>

      <ul class="row practices-list__list">
        <li
          v-for="(practice, index) in practices"
          :key="practice.id"
          :class="`${className} ${className}-${index} col-12 col-md-4 col-lg-2${isActive(
            index
          )}`"
          @mouseover="setPractice(practice, index)"
          @mouseout="resetPractice()"
        >
          <nuxt-link
            v-if="practice.full_slug"
            v-editable="blok"
            :to="'/' + practice.full_slug"
            :class="`${className}-link ${className}`"
            ><img
              :src="practice.content.intro_icon_off"
              :alt="`${practice.content.mark}: ${practice.content.title}`"
              class="practices-list__image practices-list__image--off"
            />
            <img
              :src="practice.content.intro_icon_on"
              :alt="`${practice.content.mark}: ${practice.content.title}`"
              class="practices-list__image practices-list__image--over"
            />
            <p class="practices-list__mobile-description">
              {{ blok.description }}
            </p>
          </nuxt-link>
        </li>
      </ul>

      <div
        :class="`practices-content${
          selectedPractice ? ' practices-content--active' : ''
        }`"
      >
        <div class="practices-content__header">
          <transition name="fade">
            <p v-if="selectedPractice">{{ selectedPractice.name }}</p>
          </transition>
        </div>
        <div class="practices-content__text">
          <transition name="fade">
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="selectedPractice"
              v-html="selectedPractice.content.intro"
            />
            <!--eslint-enable-->
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PracticesList',
  props: {
    blok: {
      type: Object,
      default: () => {},
    },
    visible: {
      type: Boolean,
      default: () => false,
    },
    visibleSectionIndex: {
      type: Number,
      default: () => 1000,
    },
  },
  data() {
    return {
      practices: [],
      className: 'practices-list__list-item',
      animationClass: 'practices-list--animate',
      selectedIndex: -1,
      selectedPractice: {
        name: '',
        full_slug: '',
        content: {},
      },
    }
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.animationClass =
          'practices-list--animate practices-list--animate-in'
        this.$store.commit(
          'globals/setVisibleSectionName',
          this.blok.breadcrumb
        )
        this.$store.commit(
          'globals/setVisibleSectionIndex',
          this.visibleSectionIndex
        )
      } else {
        this.animationClass =
          'practices-list--animate practices-list--animate-done'
      }
    },
  },
  mounted() {
    this.$storyapi
      .get('cdn/stories', {
        starts_with: this.blok.api ? this.blok.api : 'practices/',
        is_startpage: '0',
        cv: this.$store.state.cacheVersion,
      })
      .then((res) => {
        this.practices = res.data.stories
      })
      .catch((res) => {
        // eslint-disable-next-line no-console
        console.error('Practice List - Failed to load resource', res)
      })
  },
  methods: {
    getComponentClass(blok, animationClass) {
      const height = blok.isHScreen ? 'h-screen' : 'h-default'
      const space = blok.space ? `space--${blok.space}` : 'space--small'
      const gradient = blok.isDiagonalGradient
        ? 'practices-list--diagonal-gradient'
        : 'practices-list--vertical-gradient'

      return `practices-list theme-dark ${height} ${gradient} ${space} ${animationClass}`
    },
    isActive(index) {
      if (this.selectedIndex === index) {
        return ` ${this.className}--active`
      }
      return ''
    },
    setPractice(practice, index) {
      this.selectedIndex = index
      this.selectedPractice = practice
    },
    resetPractice() {
      this.selectedIndex = -1
      this.selectedPractice = null
    },
  },
}
</script>

<style lang="scss">
@import '../../../assets/scss/settings';

.fade-enter-active,
.fade-leave-active {
  transform: translateY(0);
  opacity: 1;
  transition: all 0.3s ease-out;
}
.fade-enter,
.fade-leave-to {
  transform: translateY(-20px);
  opacity: 0;
  transition: all 0.2s ease-in;
}

.practices-content {
  color: $white;
  display: none;
  height: 90px;
  padding: spacer(1) 0 0 0;

  &--active {
    transition-delay: 0s;

    .practices-content__text {
      transition-delay: 0.3s;
    }
  }

  &__header {
    @include font-family($font-family-headings-bold, $headings-font-weight);
    @include font-size(18);

    height: 27px;
    margin-bottom: 15px;

    p {
      margin-top: 0;
    }
  }

  &__text {
    transition-delay: 0s;
    width: 50%;
  }
}

.practices-list {
  display: flex;
  flex-direction: column;
  justify-content: center;

  &.h-default {
    min-height: 0;
  }

  &--animate {
    .practices-list__description,
    .practices-list__title {
      opacity: 0;
      transform: translate(0, 30px);
      transition: all 0.3s ease-out 0.2s;
    }
    .practices-list__description {
      transition-delay: 0.3s;
    }
    .practices-list__list-item {
      opacity: 0;
      transform: scale(0.7);
      transition: all 0.3s ease-out 0.5s;
    }
    .practices-list__list-item-1 {
      transition-delay: 0.8s;
    }
    .practices-list__list-item-2 {
      transition-delay: 1s;
    }
    .practices-list__list-item-3 {
      transition-delay: 1.2s;
    }
    .practices-list__list-item-4 {
      transition-delay: 1.4s;
    }
    .practices-list__list-item-5 {
      transition-delay: 1.6s;
    }
    &-done,
    &-in {
      .practices-list__description,
      .practices-list__title {
        opacity: 1;
        transform: translate(0, 0);
      }
      .practices-list__list-item {
        opacity: 1;
        transform: scale(1);
      }
    }
  }

  &--diagonal-gradient {
    background-image: linear-gradient(
      135deg,
      $grad-gradient 0%,
      $black 50%,
      $grad-gradient 100%
    );
  }

  &--vertical-gradient {
    background-image: linear-gradient(0deg, #131313 0%, $black 100%);
  }

  &__title {
    @include font-size(36);
    @include font-family($font-family-headings-bold, $headings-font-weight);

    margin-bottom: 0;
    margin-top: 0;
  }

  &__description {
    margin: 10px 0 20px 0;
  }

  &__mobile-description {
    padding: 0 32px;
    margin-top: 0;
    margin-bottom: 30px;
  }

  &__image {
    width: 100%;
    height: auto;

    &--over {
      display: none;
    }
  }

  &__list {
    display: flex;
    padding: spacer(1) 0;
    margin: 0;
    list-style: none;
  }

  &__list-item {
    padding: 0;
    cursor: pointer;

    &-link {
      display: block;
      margin: 0 5px 5px 0;
    }

    &:nth-child(2n) {
      .practices-list__list-item-link {
        margin: 0 0 5px 5px;
      }
    }
  }
}

@include media-breakpoint-up(md) {
  .practices-list__mobile-description {
    display: none;
  }

  .practices-list__list-item {
    &:hover {
      .practices-list__image--off {
        display: none;
      }

      .practices-list__image--over {
        display: inline-block;
      }
    }

    &:nth-child(2n) {
      .practices-list__list-item-link {
        margin: 0 5px 5px 0;
      }
    }

    &:nth-child(3n) {
      .practices-list__list-item-link {
        margin: 0 0 5px 5px;
      }
    }
  }
}
@include media-breakpoint-up(lg) {
  .practices-content {
    display: block;
  }
  .practices-list__list {
    padding: spacer(3) 0 spacer(1);

    .practices-list__list-item {
      cursor: pointer;
      flex: 0 0 15.7%;

      &:not(:last-child) {
        margin-right: 10px;
      }

      .practices-list__list-item-link {
        margin: 0;
      }
    }
  }
}
</style>
